import React, { useMemo, useState } from "react";
import { useWorkspace } from "@/context/WorkspaceContext";
import { inr } from "@/lib/api";
import { PriceTag } from "@/components/PriceTag";
import PricingEditor from "@/components/PricingEditor";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Settings2, Plus } from "lucide-react";

export default function PackageCalculator({ initial, onSave, saving }) {
  const { config } = useWorkspace();
  const [pricingOpen, setPricingOpen] = useState(false);
  const packages = config?.packages || [];
  const occupancy = config?.occupancy || [];
  const addonList = config?.addons || [];
  const visaFee = config?.visa_insurance_fee || 0;

  const [pkg, setPkg] = useState(initial?.package_id || (packages[0]?.id ?? ""));
  const [occ, setOcc] = useState(initial?.occupancy_id || (occupancy[0]?.id ?? ""));
  const [visa, setVisa] = useState(initial?.visa_insurance ?? true);
  const [addons, setAddons] = useState(initial?.addon_ids || []);

  const selPkg = packages.find((p) => p.id === pkg);
  const selOcc = occupancy.find((o) => o.id === occ);
  const selAddons = addonList.filter((a) => addons.includes(a.id));

  const total = useMemo(() => {
    let t = (selPkg?.price || 0) + (selOcc?.surcharge || 0);
    if (visa) t += visaFee;
    t += selAddons.reduce((s, a) => s + a.price, 0);
    return t;
  }, [selPkg, selOcc, visa, selAddons, visaFee]);

  const toggleAddon = (id) =>
    setAddons((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = () => {
    onSave?.({
      package: selPkg?.name,
      package_id: pkg,
      package_price: selPkg?.price || 0,
      occupancy: selOcc?.name,
      occupancy_id: occ,
      occupancy_surcharge: selOcc?.surcharge || 0,
      visa_insurance: visa,
      addons: selAddons.map((a) => ({ id: a.id, name: a.name, price: a.price })),
      addon_ids: addons,
      total,
    });
  };

  return (
    <div className="grid lg:grid-cols-5 gap-5">
      <div className="lg:col-span-3 space-y-5">
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="eyebrow text-gold">Package Tier</Label>
            <Dialog open={pricingOpen} onOpenChange={setPricingOpen}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="inline-edit-pricing-btn"
                  className="border-gold text-gold hover:bg-gold/20 font-medium text-xs h-7 gap-1 px-2.5"
                >
                  <Settings2 className="w-3.5 h-3.5 text-gold" />
                  <span>Edit / Add Packages</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#05261D] border-gold/30 max-w-3xl max-h-[85vh] overflow-auto text-white" data-testid="inline-pricing-modal">
                <DialogHeader>
                  <DialogTitle className="font-serif text-gold flex items-center gap-2 text-lg">
                    <Settings2 className="w-5 h-5" /> Edit Packages, Rooms & Add-ons
                  </DialogTitle>
                  <DialogDescription className="text-emerald-100/70 text-xs">
                    Add new Umrah packages, adjust prices, room surcharges, add-ons and visa fees.
                  </DialogDescription>
                </DialogHeader>
                <PricingEditor onSaved={() => setPricingOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mt-2">
            {packages.map((p) => (
              <button key={p.id} data-testid={`pkg-option-${p.id}`} onClick={() => setPkg(p.id)}
                className={`text-left p-3 rounded-xl border card-hover ${pkg === p.id ? "border-gold bg-gold/10 gold-glow" : "border-border/60 bg-card/40"}`}>
                <p className="text-sm font-semibold">{p.name}</p>
                <p className="mt-1"><PriceTag price={p.price} mrp={p.mrp} until={p.offer_until} /></p>
                <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{p.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="eyebrow text-gold">Room Occupancy</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
            {occupancy.map((o) => (
              <button key={o.id} data-testid={`occ-option-${o.id}`} onClick={() => setOcc(o.id)}
                className={`p-2.5 rounded-lg border text-xs ${occ === o.id ? "border-gold bg-gold/10" : "border-border/60 bg-card/40"}`}>
                <p className="font-medium">{o.name}</p>
                <p className="text-muted-foreground font-mono mt-0.5">{o.surcharge ? `+${inr(o.surcharge)}` : "Base"}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between p-3 rounded-lg border border-border/60 bg-card/40">
          <div>
            <p className="text-sm font-medium">Visa + Travel Insurance</p>
            <p className="text-xs text-muted-foreground">Umrah E-Visa processing & Ihram insurance</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-gold font-mono text-sm">{inr(visaFee)}</span>
            <Switch data-testid="visa-insurance-toggle" checked={visa} onCheckedChange={setVisa} />
          </div>
        </div>

        <div>
          <Label className="eyebrow text-gold">Premium Add-ons</Label>
          <div className="space-y-2 mt-2">
            {addonList.map((a) => (
              <label key={a.id} data-testid={`addon-option-${a.id}`}
                className="flex items-center gap-3 p-2.5 rounded-lg border border-border/60 bg-card/40 cursor-pointer">
                <Checkbox checked={addons.includes(a.id)} onCheckedChange={() => toggleAddon(a.id)} />
                <span className="text-sm flex-1">{a.name}</span>
                <span className="text-gold font-mono text-sm">{inr(a.price)}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="lg:col-span-2">
        <Card className="p-5 bg-[#05261D]/70 border-gold/20 sticky top-24">
          <p className="eyebrow text-gold">Package Summary</p>
          <div className="mt-4 space-y-2.5 text-sm">
            <Row label={selPkg?.name || "—"} value={inr(selPkg?.price || 0)} />
            {selOcc?.surcharge ? <Row label={selOcc.name} value={inr(selOcc.surcharge)} /> : null}
            {visa && <Row label="Visa + Insurance" value={inr(visaFee)} />}
            {selAddons.map((a) => <Row key={a.id} label={a.name} value={inr(a.price)} />)}
          </div>
          <div className="border-t border-border/60 mt-4 pt-4 flex items-baseline justify-between">
            <span className="text-sm text-muted-foreground">Total per pilgrim</span>
            <span data-testid="calculator-total" className="text-2xl font-serif font-semibold text-gold">{inr(total)}</span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2 font-mono">
            Partner commission is set by the company in the Commission panel.
          </p>
          {onSave && (
            <Button data-testid="calculate-package-btn" onClick={save} disabled={saving}
              className="w-full mt-4 bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">
              {saving ? "Saving…" : "Save to Customer"}
            </Button>
          )}
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground truncate pr-2">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
